import { Spinner } from '@chakra-ui/react'
import { yupResolver } from '@hookform/resolvers/yup'
import type React from 'react'
import { useEffect } from 'react'
import { type SubmitHandler, useForm } from 'react-hook-form'
import { useSelector } from 'react-redux'
import * as yup from 'yup'
import { useAcceptDisclaimersMutation } from '../redux/services/coreApi'
import type { RootState } from '../redux/store'
import DefaultModal from './DefaultModal'
import FormCheckbox from './FormCheckbox'

interface WelcomeModalProps {
  isOpen: boolean
  onClose: () => void
}

type FormData = {
  privacyPolicy: boolean
}

const WelcomeModal: React.FC<WelcomeModalProps> = ({ isOpen, onClose }) => {
  const schema: yup.SchemaOf<FormData> = yup.object({
    privacyPolicy: yup
      .boolean()
      .required('The terms must be accepted.')
      .oneOf([true], 'The terms must be accepted.'),
  })

  const {
    register,
    handleSubmit,
    formState: { errors },
    getValues,
    setValue,
  } = useForm<FormData>({
    resolver: yupResolver(schema),
    reValidateMode: 'onChange',
  })

  const [acceptDisclaimers, { isLoading: acceptDisclaimersIsLoading }] =
    useAcceptDisclaimersMutation()

  const onSubmit: SubmitHandler<FormData> = async ({ privacyPolicy }) => {
    if (privacyPolicy && !user.hasAcceptedDisclaimers) {
      await acceptDisclaimers().unwrap()
    }
    onClose()
  }

  const user = useSelector((state: RootState) => state.session.user)

  useEffect(() => {
    if (user.hasAcceptedDisclaimers) {
      setValue('privacyPolicy', true)
    }
  }, [user])

  return (
    <DefaultModal
      isOpen={isOpen}
      onClose={handleSubmit(onSubmit)}
      isLoading={false}
      title="Welcome to Penning!"
    >
      <div className="flex flex-col items-center">
        <div className="text-description m-10 mt-0 space-y-3">
          <p>
            It is great to have you here and we hope you will find your way
            around our platform with ease.
          </p>
          <p>
            We are working hard on building the world's best platform for you to
            acquire and invest in crypto and digital assets.
          </p>
          <p>
            Should you have any questions, encounter issues or need help in any
            way, do not hesitate to reach out to{' '}
            <a
              href="mailto:support@penning.io"
              className="hover:underline text-brand-blue dark:text-white"
            >
              support@penning.io
            </a>{' '}
            - we will get back to you as soon as possible.
          </p>
          <p>
            We are also always open for feedback and suggestions for
            improvements, so you are also more than welcome to send us your
            ideas; we would love to hear from you!
          </p>
          <p className="font-bold mt-4">
            IMPORTANT! BEWARE, SCAMMERS ARE OUT THERE!
          </p>
          <p>
            Unfortunately we live in a world where people are scammed and
            defrauded of USD 1 trillion every single year! And due to the
            occasional hype and market craze in the crypto and digital assets
            markets at times, some people are unfortunately approached by
            scammers who promise them quick returns with minimal risk. PLEASE!
            DO NOT PUT IN ANY ACCOUNTS OR WALLET ADDRESSES THAT ARE NOT YOUR
            OWN! If you cannot or do not know how to access your crypto-wallet,
            it is not your wallet!
          </p>
          <p>
            We want to make acquiring and investing in crypto and digital assets
            fun and safe for everyone, so please, if you have any suspicion or
            doubts about potentially being subject to a scam or fraud, do not
            hesitate to contact us! We would love to help you!
          </p>
          <FormCheckbox
            id="tos"
            renderLabel={
              <>
                I have read and understood the above, and read, understood, and
                accept:{' '}
                <a
                  target="_blank"
                  rel="noreferrer"
                  href="https://www.penning.io/terms-conditions"
                  className="hover:underline text-brand-blue dark:text-white"
                >
                  Terms and Conditions
                </a>
                ,{' '}
                <a
                  target="_blank"
                  rel="noreferrer"
                  href="https://www.penning.io/privacy-policy"
                  className="hover:underline text-brand-blue dark:text-white"
                >
                  Privacy Policy
                </a>
                ,{' '}
                <a
                  target="_blank"
                  rel="noreferrer"
                  href="https://www.penning.io/copyright-and-intellectual-property-rights"
                  className="hover:underline text-brand-blue dark:text-white"
                >
                  Copyright
                </a>
                ,{' '}
                <a
                  target="_blank"
                  rel="noreferrer"
                  href="https://www.penning.io/disclaimer"
                  className="hover:underline text-brand-blue dark:text-white"
                >
                  Disclaimer
                </a>
                .
              </>
            }
            registeredForm={register('privacyPolicy')}
            error={errors?.privacyPolicy}
          />
          <button className="button" onClick={handleSubmit(onSubmit)}>
            {acceptDisclaimersIsLoading ? (
              <Spinner size="sm" />
            ) : (
              'I understand'
            )}
          </button>
        </div>
      </div>
    </DefaultModal>
  )
}

export default WelcomeModal
